/**
 * Centralized logging utility for development and production environments.
 *
 * Development mode: All logs are output to console
 * Production mode: Only errors are logged (warnings and info suppressed)
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/utils/logger';
 *
 * logger.debug('Detailed debugging info', { data });
 * logger.log('General information', value);
 * logger.warn('Warning message', issue);
 * logger.error('Error occurred', error);
 * ```
 */

/* eslint-disable no-console */
const isDev = import.meta.env.DEV;

export const logger = {
  /**
   * Debug-level logging - only visible in development
   * Use for detailed debugging information during development
   */
  debug: (...args: unknown[]) => {
    if (isDev) console.debug(...args);
  },

  /**
   * Info-level logging - only visible in development
   * Use for general information about application flow
   */
  log: (...args: unknown[]) => {
    if (isDev) console.log(...args);
  },

  /**
   * Warning-level logging - only visible in development
   * Use for non-critical issues that should be investigated
   */
  warn: (...args: unknown[]) => {
    if (isDev) console.warn(...args);
  },

  /**
   * Error-level logging - always visible
   * Use for errors and exceptions that need attention
   *
   * Future enhancement: integrate with error tracking service (e.g., Sentry)
   */
  error: (...args: unknown[]) => {
    // Always log errors, even in production
    console.error(...args);
  },

  /**
   * Grouped logging - only visible in development
   * Use for organizing related log statements
   */
  group: (label: string) => {
    if (isDev) console.group(label);
  },

  /**
   * End grouped logging - only visible in development
   */
  groupEnd: () => {
    if (isDev) console.groupEnd();
  },
};
/* eslint-enable no-console */
